import { useTranslation } from 'react-i18next';
import { RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import type { TronEnergyOrder, TronEnergyOrderStatus } from '@/api';
import { formatUtcTimestamp } from '@/utils/formatUtcTimestamp';
import { shortenTronAddress } from '@/utils/tronAddress';
import { copyToClipboard } from '@/utils';
import { formatCompactNumber, formatUsdt, gtUsdt, USDT_SIGNIFICANT_DIGITS } from '@/utils/money';
import { DataTableLoadingOverlay } from '@/app/components/data-table/DataTableLoadingOverlay';
import { DataTableStateRow } from '@/app/components/data-table/DataTableStateRow';
import { TablePaginationFooter } from '@/app/components/data-table/TablePaginationFooter';
import { CopyIcon, SearchIcon, SelectChevronIcon } from '@/app/components/tron-energy/TronEnergyIcons';

export interface TronEnergyOrderTableProps {
  orders: TronEnergyOrder[];
  total: number;
  page: number;
  limit: number;
  status: TronEnergyOrderStatus | '';
  keyword: string;
  isLoading: boolean;
  isPollingError: boolean;
  onPageChange: (page: number) => void;
  onStatusChange: (status: TronEnergyOrderStatus | '') => void;
  onKeywordChange: (keyword: string) => void;
  onRefresh: () => void;
}

const TABLE_COL_COUNT = 9;

const filterControlClass =
  'h-[42px] rounded-2xl border border-slate-200 bg-white text-sm font-medium text-slate-900 outline-none focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 dark:border-[#1d293d] dark:bg-[#1a1d1f] dark:text-white dark:focus:border-primary dark:focus:ring-primary/40';

function UtcTimeCell({
  value,
  align = 'left',
}: {
  value?: number | null;
  align?: 'left' | 'right';
}) {
  const formatted = formatUtcTimestamp(value);
  if (formatted === '-') {
    return <span>-</span>;
  }
  const [date, time] = formatted.split(' ');
  return (
    <div
      className={`flex flex-col gap-1 text-sm leading-none font-medium ${
        align === 'right' ? 'items-end' : 'items-start'
      }`}
    >
      <span>{date}</span>
      <span>{time}</span>
    </div>
  );
}

export function TronEnergyOrderTable({
  orders,
  total,
  page,
  limit,
  status,
  keyword,
  isLoading,
  isPollingError,
  onPageChange,
  onStatusChange,
  onKeywordChange,
  onRefresh,
}: TronEnergyOrderTableProps) {
  const { t } = useTranslation();

  const handleCopy = (text: string, label: string) => {
    copyToClipboard(text).then(() => {
      toast.success(t('common.copied', `${label}已复制到剪贴板`));
    });
  };

  const getStatusBadge = (orderStatus: TronEnergyOrderStatus) => {
    const baseClass =
      'inline-flex items-center justify-center rounded-full px-2.5 py-1.5 text-[11px] leading-none font-medium';
    switch (orderStatus) {
      case 'processing':
        return (
          <span
            className={`${baseClass} border border-amber-200 bg-amber-50 text-amber-600 dark:border-amber-500/20 dark:bg-amber-500/10 dark:text-amber-500`}
          >
            {t('tronEnergy.status.processing', '处理中')}
          </span>
        );
      case 'success':
        return (
          <span
            className={`${baseClass} border border-emerald-200 bg-emerald-50 text-emerald-600 dark:border-primary/20 dark:bg-primary/10 dark:text-primary`}
          >
            {t('tronEnergy.status.success', '成功')}
          </span>
        );
      case 'failed':
        return (
          <span
            className={`${baseClass} border border-red-200 bg-red-50 text-red-600 dark:border-red-500/20 dark:bg-red-500/10 dark:text-red-500`}
          >
            {t('tronEnergy.status.failed', '失败')}
          </span>
        );
      default:
        return (
          <span
            className={`${baseClass} bg-slate-100 text-slate-500 dark:bg-[#D2F0E1]/5 dark:text-slate-400`}
          >
            {orderStatus}
          </span>
        );
    }
  };

  const totalPages = Math.max(1, Math.ceil(total / limit));

  return (
    <div className="overflow-hidden rounded-2xl border border-slate-200 bg-slate-50 dark:border-[#D2F0E1]/5 dark:bg-[#1a1d1f]">
      <div className="flex flex-col gap-4 border-b border-slate-200 p-4 lg:flex-row lg:items-center lg:gap-10 dark:border-[#1d293d]">
        <div className="flex min-w-0 flex-1 items-center gap-1.5">
          <span className="h-[18px] w-[5px] shrink-0 rounded-full bg-emerald-600 dark:bg-primary" />
          <h2 className="text-lg leading-none font-bold whitespace-nowrap text-slate-900 dark:text-white">
            {t('tronEnergy.table.title', '订单明细')}
          </h2>
          <span className="hidden text-xs font-medium text-slate-400 sm:inline dark:text-slate-500">
            ({t('tronEnergy.table.autoRefreshHint', '每 3 秒自动刷新')})
          </span>
          {isPollingError && (
            <span className="text-xs font-medium text-amber-500 animate-pulse">
              {t('tronEnergy.table.pollingWarning', '网络连接不稳定，正在重试...')}
            </span>
          )}
        </div>

        <div className="flex min-w-0 flex-1 flex-wrap items-center gap-2.5">
          <div className="flex items-center gap-2.5">
            <span className="text-sm leading-none font-medium whitespace-nowrap text-slate-500 dark:text-slate-400">
              {t('tronEnergy.table.status', '状态')}:
            </span>
            <div className="relative">
              <select
                value={status}
                onChange={(e) => onStatusChange(e.target.value as TronEnergyOrderStatus | '')}
                className={`${filterControlClass} appearance-none py-1.5 pr-9 pl-3.5`}
              >
                <option value="">{t('tronEnergy.filters.allStatuses', '全部')}</option>
                <option value="processing">{t('tronEnergy.status.processing', '处理中')}</option>
                <option value="success">{t('tronEnergy.status.success', '成功')}</option>
                <option value="failed">{t('tronEnergy.status.failed', '失败')}</option>
              </select>
              <span className="pointer-events-none absolute top-1/2 right-3.5 flex size-4 -translate-y-1/2 items-center justify-center">
                <SelectChevronIcon className="size-2 rotate-90 text-slate-400 dark:text-slate-200" />
              </span>
            </div>
          </div>

          <div className="relative min-w-[200px] flex-1">
            <SearchIcon className="pointer-events-none absolute top-1/2 left-3.5 size-4 -translate-y-1/2 text-slate-400" />
            <input
              type="text"
              value={keyword}
              onChange={(e) => onKeywordChange(e.target.value)}
              placeholder={t('tronEnergy.filters.keywordPlaceholder', '搜索订单号 / 充值地址')}
              className={`${filterControlClass} w-full py-2.5 pr-4 pl-10 font-normal placeholder:text-slate-400`}
            />
          </div>

          <button
            type="button"
            onClick={onRefresh}
            disabled={isLoading}
            className={`${filterControlClass} flex size-[42px] shrink-0 items-center justify-center p-0 text-slate-500 hover:text-slate-900 disabled:opacity-50 dark:text-slate-400 dark:hover:text-white`}
            title={t('common.refresh', '刷新')}
          >
            <RefreshCw className={`size-4 ${isLoading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      <div className="relative overflow-x-auto">
        <DataTableLoadingOverlay loading={isLoading && orders.length > 0} />
        <table className="w-full min-w-[1080px] border-collapse text-left">
          <thead>
            <tr className="border-b border-slate-200 bg-slate-100/80 text-xs font-medium text-slate-500 dark:border-[#1d293d] dark:bg-[#0E0F11]/80 dark:text-slate-400">
              <th className="h-[62px] py-4 pr-2 pl-6 whitespace-nowrap">
                {t('tronEnergy.table.orderNo', '订单号')}
              </th>
              <th className="h-[62px] px-2 py-4 whitespace-nowrap">
                {t('tronEnergy.table.rechargeAddress', '能量充值地址')}
              </th>
              <th className="h-[62px] px-2 py-4 whitespace-nowrap">
                {t('tronEnergy.table.status', '状态')}
              </th>
              <th className="h-[62px] px-2 py-4 whitespace-nowrap">
                {t('tronEnergy.table.paidAmount', '支付积分 / 金额')}
              </th>
              <th className="h-[62px] px-2 py-4 whitespace-nowrap">
                {t('tronEnergy.table.rentedEnergy', '租用量')}
              </th>
              {/* Completed energy column is hidden until the value is reliable.
              <th className="h-[62px] px-2 py-4 whitespace-nowrap">{t('tronEnergy.table.completedEnergy', '已完成租用量')}</th>
              */}
              <th className="h-[62px] px-2 py-4 whitespace-nowrap">
                {t('tronEnergy.table.rentDuration', '租用时长')}
              </th>
              <th className="h-[62px] px-2 py-4 whitespace-nowrap">
                {t('tronEnergy.table.paidTime', '支付时间 (UTC)')}
              </th>
              <th className="h-[62px] px-2 py-4 whitespace-nowrap">
                {t('tronEnergy.table.delegateTime', '代理时间 (UTC)')}
              </th>
              <th className="h-[62px] py-4 pr-6 pl-2 text-right whitespace-nowrap">
                {t('tronEnergy.table.expireTime', '到期时间 (UTC)')}
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-200 dark:divide-[#1d293d]">
            <DataTableStateRow
              colSpan={TABLE_COL_COUNT}
              loading={isLoading && orders.length === 0}
              isEmpty={!isLoading && orders.length === 0}
              emptyTitle={t('common.noData', '暂无订单记录')}
            />
            {orders.map((order) => (
              <tr
                key={order.orderNo}
                className="text-sm font-medium text-slate-500 transition-colors hover:bg-slate-100/60 dark:text-slate-400 dark:hover:bg-[#0E0F11]/40"
              >
                <td className="h-[72px] py-3 pr-2 pl-6 whitespace-nowrap">
                  <div className="flex items-center gap-1">
                    <span>{order.orderNo}</span>
                    <button
                      type="button"
                      onClick={() => handleCopy(order.orderNo, '订单号')}
                      className="text-slate-400 hover:text-emerald-600 dark:hover:text-primary"
                    >
                      <CopyIcon className="size-3.5" />
                    </button>
                  </div>
                </td>
                <td className="h-[72px] px-2 py-3 whitespace-nowrap">
                  <div className="flex items-center gap-1">
                    <span>{shortenTronAddress(order.rechargeAddress)}</span>
                    <button
                      type="button"
                      onClick={() => handleCopy(order.rechargeAddress, '充值地址')}
                      className="text-slate-400 hover:text-emerald-600 dark:hover:text-primary"
                    >
                      <CopyIcon className="size-3.5" />
                    </button>
                  </div>
                </td>
                <td className="h-[72px] px-2 py-3 whitespace-nowrap">
                  {getStatusBadge(order.status)}
                </td>
                <td className="h-[72px] px-2 py-3 whitespace-nowrap">
                  <div className="flex flex-col gap-0.5 text-xs leading-none font-medium">
                    <div className="flex items-start gap-0.5">
                      <span className="text-slate-900 dark:text-slate-200">
                        {formatCompactNumber(order.paidPoints)}
                      </span>
                      <span className="text-slate-500 dark:text-slate-400">
                        {t('tronEnergy.units.points', '积分')}
                      </span>
                    </div>
                    {gtUsdt(order.paidUsdt, 0) && (
                      <div className="flex items-start gap-0.5">
                        <span className="text-slate-900 dark:text-slate-200">
                          {formatUsdt(order.paidUsdt, {
                            significantDigits: USDT_SIGNIFICANT_DIGITS,
                          })}
                        </span>
                        <span className="text-slate-500 dark:text-slate-400">USDT</span>
                      </div>
                    )}
                  </div>
                </td>
                <td className="h-[72px] px-2 py-3 whitespace-nowrap">
                  <div className="flex items-start gap-0.5 text-xs leading-none font-medium">
                    <span className="text-slate-900 dark:text-slate-200">
                      {formatCompactNumber(order.rentedEnergy)}
                    </span>
                    <span className="text-slate-500 dark:text-slate-400">
                      {t('tronEnergy.units.energy', 'Energy')}
                    </span>
                  </div>
                </td>
                {/* Completed energy column is hidden until the value is reliable.
                <td className="h-[72px] px-2 py-3 whitespace-nowrap">
                  <div className="flex items-start gap-0.5 text-xs leading-none font-medium">
                    <span className="text-slate-900 dark:text-slate-200">
                      {formatCompactNumber(order.completedEnergy)}
                    </span>
                    <span className="text-slate-500 dark:text-slate-400">
                      {t('tronEnergy.units.energy', 'Energy')}
                    </span>
                  </div>
                </td>
                */}
                <td className="h-[72px] px-2 py-3 whitespace-nowrap">
                  <div className="flex items-center gap-1 text-sm leading-none font-medium">
                    <span>{order.durationHours}</span>
                    <span>{t('tronEnergy.units.hour', '小时')}</span>
                  </div>
                </td>
                <td className="h-[72px] px-2 py-3 whitespace-nowrap">
                  <UtcTimeCell value={order.paidTime} />
                </td>
                <td className="h-[72px] px-2 py-3 whitespace-nowrap">
                  <UtcTimeCell value={order.delegateTime} />
                </td>
                <td className="h-[72px] py-3 pr-6 pl-2 whitespace-nowrap">
                  <UtcTimeCell value={order.expireTime} align="right" />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <TablePaginationFooter
        page={page}
        totalPages={totalPages}
        totalItems={total}
        onPageChange={onPageChange}
        className="m-0 px-4 pb-4"
      />
    </div>
  );
}
